import { useState } from "react";
import { BaseFileInput } from "components/DesignSystem/BaseFileInput/BaseFileInput";
import { useToast } from "hooks/useToast";
import { useUploadIncomeStatementMutation } from "store/apis/estimatedTax";

type Props = {
  entityId: string;
  year: string;
  quarter?: number;
};

export const InlineIncomeStatementUpload = ({
  entityId,
  year,
  quarter,
}: Props) => {
  const { promise } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [uploadIncomeStatement, { isLoading: isUploading }] =
    useUploadIncomeStatementMutation();

  const handleDrop = async (dropped: File[]) => {
    const selected = dropped[0];
    if (!selected) return;
    setFile(selected);

    const formData = new FormData();
    formData.append("files", selected);
    formData.append("tax_year", year);
    if (quarter) {
      formData.append("quarter", String(quarter));
    }

    const uploadPromise = uploadIncomeStatement({
      entityId,
      formData,
    }).unwrap();

    promise(uploadPromise, {
      loading: `Syncing income statement for ${year}…`,
      success: `Income statement synced for ${year}`,
      error: (e: unknown) => {
        const err = e as { data?: { error?: string } };
        return err.data?.error || "Failed to extract data from the file";
      },
    });

    try {
      await uploadPromise;
    } catch {
      setFile(null);
    }
  };

  return (
    <BaseFileInput
      label={`Upload income statement for FY ${year}`}
      required
      file={file}
      isUploading={isUploading}
      onDrop={handleDrop}
      onDelete={() => setFile(null)}
      accept={{
        "application/pdf": [],
        "text/csv": [],
        "image/png": [],
        "image/jpeg": [],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
        "application/vnd.ms-excel": [],
      }}
      block
    />
  );
};
